import { FIRESTORE_ENABLED } from '@/config/env';
import { clearBrandColors } from '@/features/organization/brandTheme';
import { type AuthUser, signOutOfFirebase, watchIdToken } from '@/lib/firebase';
import { resetPendingWrites } from '@/lib/pendingWrites';
import { readNullableString, readRecord, readRecords, readString } from '@/lib/readers';
import { parseMembership } from './types';

const STORAGE_KEY = 'edify_session';

/** The prefix of each stored key that the end of a session removes. */
export const SESSION_KEY_PREFIX = 'edify:session:';

/** Increase this number when the stored shape or the permission vocabulary changes.
 *  The app then removes an older session and the user signs in again. */
const SESSION_VERSION = 3;

/** Why the last session ended. */
export type SessionEnd = 'requested' | 'expired' | 'denied' | 'revoked';

export interface SessionConfig {
  /** Run when a session ends, for state held outside this store. */
  onSessionEnd?: () => void;
}

let config: SessionConfig = {};

/** Sets what the store calls when a session ends. */
export function configureSession(next: SessionConfig) {
  config = next;
}

export type UserRole = 'admin' | 'member';

export interface Membership {
  organizationId: string;
  role: UserRole;
  permissions: string[];
}

export interface Session {
  token: string;
  name: string;
  email: string;
  organizationId: string | null;
  memberships: Membership[];
}

function read(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persist(entry: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...entry, version: SESSION_VERSION }));
}

/** Returns the stored session, or null for a malformed one or one written under an older shape. */
function parseSession(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const value = readRecord(JSON.parse(raw));
    if (value.version !== SESSION_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    const token = readString(value.token);
    if (!token) return null;
    return {
      token,
      name: readString(value.name),
      email: readString(value.email),
      organizationId: readNullableString(value.organizationId),
      memberships: readRecords(value.memberships)
        .map((entry) => parseMembership({ ...entry, organization_id: entry.organizationId }))
        .filter((entry): entry is Membership => entry !== null),
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

let current: Session | null = null;
let restored = false;
let endedBecause: SessionEnd | null = null;
const listeners = new Set<() => void>();

/** Returns the stored session, read the first time anything asks for it. */
export function getSession(): Session | null {
  if (!restored) {
    restored = true;
    current = parseSession(read());
  }
  return current;
}

function hold(next: Session | null): void {
  current = next;
  restored = true;
}

function emit() {
  for (const listener of [...listeners]) listener();
}

/** Removes the state held for a session outside this store. */
function clearSessionState(): void {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(SESSION_KEY_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
  resetPendingWrites();
  clearBrandColors();
  config.onSessionEnd?.();
}

/** Takes the session another tab stored, and clears the state when that session is not this one. */
function onStorage(event: StorageEvent): void {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  const previous = getSession();
  const next = parseSession(read());
  hold(next);
  if (previous && next?.email !== previous.email) clearSessionState();
  emit();
}

/** Calls the listener on each change of the session. Returns the function that stops it. */
export function subscribe(listener: () => void) {
  if (!listeners.size) window.addEventListener('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) window.removeEventListener('storage', onStorage);
  };
}

/** Stores a new session and tells every listener. */
export function signIn(next: Session) {
  hold(next);
  endedBecause = null;
  persist(next);
  emit();
}

/** Removes the session and the state held for it, then tells every listener. */
export function signOut(reason: SessionEnd = 'requested') {
  hold(null);
  endedBecause = reason;
  localStorage.removeItem(STORAGE_KEY);
  clearSessionState();
  emit();
}

/** Stores changed memberships in the session. Equal memberships change nothing. */
export function updateMemberships(memberships: Membership[]): void {
  const held = getSession();
  if (!held) return;
  if (JSON.stringify(held.memberships) === JSON.stringify(memberships)) return;
  hold({ ...held, memberships });
  persist({ ...held, memberships });
  emit();
}

function updateToken(token: string): void {
  const held = getSession();
  if (!held || held.token === token) return;
  hold({ ...held, token });
  persist({ ...held, token });
  emit();
}

/** Returns the token of the stored session, or null without one. */
export function getAuthToken(): string | null {
  return getSession()?.token ?? null;
}

/** Returns what ended the last session, or null once a new one starts. */
export function getSessionEnd(): SessionEnd | null {
  return endedBecause;
}

/** Returns the membership of the selected organization, or null without one. */
export function activeMembership(session: Session | null): Membership | null {
  if (!session?.organizationId) return null;
  return (
    session.memberships.find((entry) => entry.organizationId === session.organizationId) ?? null
  );
}

/** Signs the user out of Firebase. */
export async function endFirebaseSession(): Promise<void> {
  if (!FIRESTORE_ENABLED) return;
  await signOutOfFirebase();
}

async function applyFirebaseUser(user: AuthUser | null): Promise<void> {
  if (!user) {
    if (getSession()) signOut('expired');
    return;
  }
  try {
    updateToken(await user.getIdToken());
  } catch {
    signOut('expired');
  }
}

/** Keeps the stored token current, and ends the session when Firebase signs the user out. */
export async function watchFirebaseSession(): Promise<void> {
  if (!FIRESTORE_ENABLED) return;

  await new Promise<void>((resolve, reject) => {
    watchIdToken((user) => {
      void applyFirebaseUser(user).finally(resolve);
    }).catch(reject);
  });
}
